import { supabase } from './supabaseClient';
import { describeError } from './errorMessage';
import { EinweisungGruppe, EinweisungPublic } from '../components/sige-plan/types';

/**
 * Die ANONYME Teilnehmerseite `#/einweisung/<linkToken>` — nur RPCs, keine
 * Tabelle (docs/sige-einweisung-online-plan.md §3, §4). `anon` hat auf
 * `sige_einweisungen` und `sige_einweisung_teilnahmen` keinerlei Rechte; alles
 * laeuft ueber `security definer`-Funktionen, die den Token pruefen (Muster
 * sifaPublicService.ts / supabase/sifa_unterweisungen_rpc.sql).
 *
 * RPC-Namen und Parameter EXAKT wie in supabase/sige_einweisung_online_rpc.sql
 * (Nr. 98). Wer hier einen Parameter umbenennt, bricht die Seite still: die
 * Datenbank antwortet dann mit PGRST202 „could not find the function".
 *
 * Die oeffentliche RPC liefert NIE Teilnehmer, Unterschriften oder
 * Buchungsfelder — und dieses Modul nimmt auch nichts davon entgegen.
 */

type Zeile = Record<string, unknown>;

const str = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));
const strOrNull = (v: unknown): string | null => (v == null ? null : str(v));
const num = (v: unknown, fallback: number): number => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
};
const arr = <T>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);

/** Erster gesetzter Wert unter mehreren Schluesseln — die RPC darf snake ODER camel liefern. */
const pick = (row: Zeile, ...keys: string[]): unknown => {
  for (const k of keys) if (row[k] !== undefined && row[k] !== null) return row[k];
  return undefined;
};

/**
 * Fehler mit dem SQLSTATE der RPC (`P0002` = Link ungueltig/abgelaufen,
 * `54000` = Mengenbremse, `22023` = Eingabe abgelehnt). Die Seite entscheidet
 * daran, was sie sagt — der Klartext allein ist fuer Menschen.
 */
export class EinweisungRpcFehler extends Error {
  code: string | null;
  constructor(message: string, code: string | null) {
    super(message);
    this.name = 'EinweisungRpcFehler';
    this.code = code;
  }
}

/** SQLSTATE eines Fehlers, wenn er einen traegt (Supabase-Fehlerobjekt oder EinweisungRpcFehler). */
export const einweisungFehlerCode = (e: unknown): string | null => {
  const code = (e as { code?: unknown } | null)?.code;
  return typeof code === 'string' && code ? code : null;
};

const werfe = (e: unknown): never => {
  throw new EinweisungRpcFehler(describeError(e), einweisungFehlerCode(e));
};

/**
 * Migration Nr. 98 noch nicht gefahren: PostgREST kennt die Funktion nicht
 * (PGRST202). Fuer den anonymen Besucher ist das dasselbe wie ein ungueltiger
 * Link — er bekommt keine Diagnose.
 */
const istFehlendeFunktion = (e: unknown): boolean => {
  const code = einweisungFehlerCode(e);
  const msg = typeof e === 'object' && e !== null ? str((e as { message?: unknown }).message) : '';
  return code === 'PGRST202' || code === '42883' || /could not find the function/i.test(msg);
};

/** jsonb-Antwort einer RPC als Objekt; Zeichenketten werden geparst, alles andere ist null. */
const alsObjekt = (data: unknown): Zeile | null => {
  if (data == null) return null;
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      return parsed && typeof parsed === 'object' ? (parsed as Zeile) : null;
    } catch {
      return null;
    }
  }
  return typeof data === 'object' ? (data as Zeile) : null;
};

/** Gruppen defensiv: nur Objekte mit Punkte-Array, Punkte nur als Text. */
const mapGruppe = (g: Zeile): EinweisungGruppe => ({
  id: str(g.id),
  titel: str(g.titel),
  punkte: arr<unknown>(g.punkte).map((p) => str(p)),
});

export const mapEinweisungPublic = (row: Zeile): EinweisungPublic => ({
  einweisungId: str(pick(row, 'einweisungId', 'einweisung_id', 'id')),
  nummer: num(pick(row, 'nummer'), 0),
  titel: strOrNull(row.titel),
  datum: str(row.datum),
  uhrzeit: strOrNull(row.uhrzeit),
  ort: strOrNull(row.ort),
  durchfuehrenderName: strOrNull(pick(row, 'durchfuehrenderName', 'durchfuehrender_name')),
  planNr: strOrNull(pick(row, 'planNr', 'plan_nr')),
  planStand: strOrNull(pick(row, 'planStand', 'plan_stand')),
  punkte: arr<unknown>(row.punkte)
    .filter((g): g is Zeile => !!g && typeof g === 'object')
    .map(mapGruppe),
  projektName: strOrNull(pick(row, 'projektName', 'projekt_name', 'project_name')),
  bauvorhabenAdresse: strOrNull(pick(row, 'bauvorhabenAdresse', 'bauvorhaben_adresse')),
  bueroName: strOrNull(pick(row, 'bueroName', 'buero_name', 'companyName', 'company_name')),
});

// ─── Blatt laden ────────────────────────────────────────────────────────────

/**
 * Das eingefrorene Blatt zum Link (RPC `sige_einweisung_public`). `null`,
 * wenn der Token unbekannt, der Link abgelaufen oder das Blatt nicht
 * abgeschlossen ist — ODER die Migration noch fehlt. Die Seite sagt in allen
 * Faellen „Dieser Link ist nicht mehr gültig" und verraet nicht, ob es die
 * Einweisung gibt.
 */
export const ladeEinweisungPublic = async (linkToken: string): Promise<EinweisungPublic | null> => {
  const token = (linkToken || '').trim();
  if (!token) return null;
  try {
    const { data, error } = await supabase.rpc('sige_einweisung_public', { p_link_token: token });
    if (error) throw error;
    const row = alsObjekt(data);
    return row ? mapEinweisungPublic(row) : null;
  } catch (e) {
    if (istFehlendeFunktion(e)) return null;
    return werfe(e);
  }
};

// ─── Teilnahme abschliessen ─────────────────────────────────────────────────

export interface OnlineTeilnahmeEingabe {
  vorname: string;
  nachname: string;
  /** Freitext, darf leer bleiben. */
  funktion: string;
  firma: string;
  /** PNG-DataURL des SignaturePads. */
  unterschrift: string;
  userAgent?: string;
}

export interface OnlineTeilnahmeErgebnis {
  ok: true;
  /** true = dieselbe Person (Vorname, Nachname, Firma) war schon eingetragen; es wurde NICHTS geschrieben. */
  bereits: boolean;
  teilnahmeId: string;
  /** ISO-Zeitstempel der (ersten) Unterschrift. */
  unterschriebenAm: string;
}

/**
 * Traegt die Person ein (RPC `sige_einweisung_teilnahme_abschliessen`): die
 * Datenbank prueft den Link, die Laengen, die Unterschrift und die
 * Mengenbremse und schreibt `on conflict … do nothing` — ein zweiter Scan
 * derselben Person ist kein Fehler, sondern `bereits: true`.
 *
 * Fehler kommen als EinweisungRpcFehler mit SQLSTATE und Klartext aus
 * Postgres durch (P0002 Link ungueltig, 54000 zu viele, 22023 Eingabe).
 */
export const schliesseOnlineTeilnahmeAb = async (
  linkToken: string,
  eingabe: OnlineTeilnahmeEingabe,
): Promise<OnlineTeilnahmeErgebnis> => {
  const token = (linkToken || '').trim();
  if (!token) throw new EinweisungRpcFehler('Dieser Link ist nicht gültig.', 'P0002');
  if (!(eingabe.unterschrift || '').trim()) throw new Error('Bitte zuerst unterschreiben.');

  const ua = (
    eingabe.userAgent ?? (typeof navigator !== 'undefined' ? navigator.userAgent : '') ?? ''
  ).slice(0, 500);

  const { data, error } = await supabase.rpc('sige_einweisung_teilnahme_abschliessen', {
    p_link_token: token,
    p_vorname: (eingabe.vorname || '').trim(),
    p_nachname: (eingabe.nachname || '').trim(),
    // Leer geht als NULL — die Spalte ist optional, '' waere ein leerer Text.
    p_funktion: (eingabe.funktion || '').trim() || null,
    p_firma: (eingabe.firma || '').trim(),
    p_unterschrift: eingabe.unterschrift,
    p_user_agent: ua || null,
  });
  if (error) return werfe(error);

  const row = alsObjekt(data);
  if (!row) throw new Error('Der Eintrag wurde nicht bestätigt — bitte erneut versuchen.');
  return {
    ok: true,
    bereits: pick(row, 'bereits') === true,
    teilnahmeId: str(pick(row, 'teilnahmeId', 'teilnahme_id')),
    unterschriebenAm: str(pick(row, 'unterschriebenAm', 'unterschrieben_am')),
  };
};
